// Goods receipts + weighbridge tickets registers, with **Reverse & clone** on
// POSTED docs (BUILD_SPEC.md §4 core principle #1: "posted documents have a
// one-click 'reverse & clone to draft' path"; plan.md QC fix #10). Plan.md G20.
// Entry point for the landed-cost allocation screen (G-WebB) lives here too —
// that feature has no tab of its own (not in purchasing-intake.tsx's TABS
// list), so it's reached via the button below instead.
import { useMemo, useState } from 'react';
import { Alert, Button, Group, Paper, Select, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { IconAlertCircle, IconArrowBackUp, IconCoins } from '@tabler/icons-react';
import { ApiRequestError } from '../../api/client';
import { useCanOperate } from '../../auth/permissions';
import { isMassLine, type GoodsReceiptResponse, type GoodsReceiptWrite, type TicketResponse } from '@zebra/core';
import { ConfirmButton } from '../../components/ConfirmButton';
import { DocStatusBadge } from '../../components/DocStatusBadge';
import { PageHeader } from '../../components/PageHeader';
import { GoodsReceiptDraftModal } from './GoodsReceiptDraftModal';
import { formatQtyDisplay } from './common';
import {
  useCreateGoodsReceipt,
  useCreateTicket,
  useGoodsReceipts,
  useLocations,
  usePostTicket,
  usePurchaseOrder,
  useReverseGoodsReceipt,
  useReverseTicket,
  useTickets,
} from './api';

function PoNumberBadge({ poId }: { poId: string }) {
  const { data } = usePurchaseOrder(poId);
  return <Text size="sm">{data?.number ?? poId.slice(0, 8)}</Text>;
}

/** A GR's total received quantity for the register column — mass kg and EACH
 * units totalled separately with explicit unit labels (a count line's `qtyKg`
 * is null; the old `Number(l.qtyKg)` sum silently rendered an EACH-only GR as
 * "0.000 kg" — gate fix round 2026-07-07). */
function formatGrQtyTotals(gr: GoodsReceiptResponse): string {
  let kg = 0;
  let units = 0;
  for (const l of gr.lines) {
    if (isMassLine(l)) kg += Number(l.qtyKg);
    else units += Number(l.qty);
  }
  const parts: string[] = [];
  if (kg > 0 || units === 0) parts.push(`${formatQtyDisplay(kg.toFixed(3))} kg`);
  if (units > 0) parts.push(`${formatQtyDisplay(units, 0)} units`);
  return parts.join(' + ');
}

function GoodsReceiptRowActions({
  gr,
  onOpenDraft,
}: {
  gr: GoodsReceiptResponse;
  onOpenDraft: (id: string) => void;
}) {
  const canOperate = useCanOperate();
  const reverse = useReverseGoodsReceipt(gr.id);
  const createClone = useCreateGoodsReceipt();
  const [error, setError] = useState<string | null>(null);

  async function handleReverseAndClone() {
    setError(null);
    try {
      await reverse.mutateAsync();
      const body: GoodsReceiptWrite = {
        poId: gr.poId,
        ticketId: null,
        locationId: gr.locationId,
        receivedAt: new Date(),
        notes: `Reversed & cloned from ${gr.number}`,
        // Mode-aware clone body (BLOCKING gate fix 2026-07-07): a count (EACH)
        // line has qtyKg null and must clone as {qty} — sending the mass shape
        // made the clone POST 422 AFTER the reverse had already committed,
        // orphaning the reversal with no draft and a misattributed error.
        lines: gr.lines.map((l) =>
          isMassLine(l)
            ? { poLineId: l.poLineId, productId: l.productId, qtyKg: l.qtyKg!, mcPct: l.mcPct }
            : { poLineId: l.poLineId, productId: l.productId, qty: l.qty! },
        ),
      };
      const clone = await createClone.mutateAsync(body);
      onOpenDraft(clone.id);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to reverse this goods receipt');
    }
  }

  if (gr.status === 'DRAFT') {
    return (
      <Button size="xs" variant="light" onClick={() => onOpenDraft(gr.id)}>
        Continue
      </Button>
    );
  }
  if (gr.status === 'POSTED') {
    return (
      <Stack gap={2}>
        <Group gap="xs" wrap="nowrap">
          {/* Read-only view of the posted lines (mode-aware qty/mc/payable +
              the moisture-override badge, G-WebA) — opens the SAME modal as a
              DRAFT's "Continue", which self-detects readOnly from the loaded
              GR's status. */}
          <Button size="xs" variant="subtle" onClick={() => onOpenDraft(gr.id)}>
            View
          </Button>
          <Tooltip label="Requires Operations role" disabled={canOperate}>
            <span>
              <ConfirmButton
                size="xs"
                color="red"
                variant="light"
                leftSection={<IconArrowBackUp size={14} />}
                loading={reverse.isPending || createClone.isPending}
                disabled={!canOperate}
                label="Reverse & clone"
                message={`Reverse goods receipt ${gr.number}? Stock and cost will be backed out by a reversing ledger entry, and a new DRAFT copy will be created for you to redo.`}
                onConfirm={handleReverseAndClone}
              />
            </span>
          </Tooltip>
        </Group>
        {error && (
          <Text size="xs" c="red">
            {error}
          </Text>
        )}
      </Stack>
    );
  }
  if (gr.status === 'REVERSED') {
    return (
      <Button size="xs" variant="subtle" onClick={() => onOpenDraft(gr.id)}>
        View
      </Button>
    );
  }
  return null;
}

function GoodsReceiptsTable() {
  const [status, setStatus] = useState<'DRAFT' | 'POSTED' | 'REVERSED' | null>(null);
  const { data, isLoading, isError } = useGoodsReceipts({ status: status ?? undefined });
  const { data: locations } = useLocations();
  const [draftModalId, setDraftModalId] = useState<string | null>(null);

  const locationName = useMemo(() => new Map((locations?.items ?? []).map((l) => [l.id, l.name])), [locations]);
  const rows = useMemo(() => [...(data?.items ?? [])].reverse(), [data]);

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Title order={3}>Goods receipts</Title>
        <Select
          placeholder="All statuses"
          clearable
          data={['DRAFT', 'POSTED', 'REVERSED']}
          value={status}
          onChange={(v) => setStatus(v as typeof status)}
          w={160}
        />
      </Group>
      {isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load goods receipts.
        </Alert>
      )}
      <Paper withBorder radius="md">
        <Table.ScrollContainer minWidth={800}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Number</Table.Th>
                <Table.Th>PO</Table.Th>
                <Table.Th>Received at</Table.Th>
                <Table.Th>Location</Table.Th>
                <Table.Th>Qty</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((gr) => (
                <Table.Tr key={gr.id}>
                  <Table.Td>{gr.number}</Table.Td>
                  <Table.Td>
                    <PoNumberBadge poId={gr.poId} />
                  </Table.Td>
                  <Table.Td>{new Date(gr.receivedAt).toLocaleString()}</Table.Td>
                  <Table.Td>{locationName.get(gr.locationId) ?? gr.locationId}</Table.Td>
                  <Table.Td>{formatGrQtyTotals(gr)}</Table.Td>
                  <Table.Td>
                    <DocStatusBadge status={gr.status} />
                  </Table.Td>
                  <Table.Td>
                    <GoodsReceiptRowActions gr={gr} onOpenDraft={setDraftModalId} />
                  </Table.Td>
                </Table.Tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c="dimmed" ta="center" py="md">
                      No goods receipts yet — use "Receive a load" to create one.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
      <GoodsReceiptDraftModal grId={draftModalId} onClose={() => setDraftModalId(null)} />
    </Stack>
  );
}

function TicketRowActions({ ticket }: { ticket: TicketResponse }) {
  const canOperate = useCanOperate();
  const post = usePostTicket(ticket.id);
  const reverse = useReverseTicket(ticket.id);
  const createClone = useCreateTicket();
  const [error, setError] = useState<string | null>(null);
  const [cloned, setCloned] = useState<string | null>(null);

  async function handleReverseAndClone() {
    setError(null);
    setCloned(null);
    try {
      await reverse.mutateAsync();
      const clone = await createClone.mutateAsync({
        siteId: ticket.siteId,
        direction: ticket.direction,
        externalTicketNo: null,
        vehicleReg: ticket.vehicleReg,
        haulier: ticket.haulier,
        driver: ticket.driver,
        grossKg: ticket.grossKg,
        tareKg: ticket.tareKg,
        mcPct: ticket.mcPct,
        mcSource: ticket.mcSource,
        weighedAt: new Date(),
        purchaseOrderId: ticket.purchaseOrderId,
        salesOrderId: ticket.salesOrderId,
        customerId: ticket.customerId,
        productId: ticket.productId,
        notes: `Reversed & cloned from ${ticket.number}`,
      });
      setCloned(clone.number);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to reverse this ticket');
    }
  }

  if (ticket.status === 'DRAFT') {
    return (
      <Tooltip label="Requires Operations role" disabled={canOperate}>
        <span>
          <Button size="xs" disabled={!canOperate} loading={post.isPending} onClick={() => post.mutate()}>
            Post
          </Button>
        </span>
      </Tooltip>
    );
  }
  if (ticket.status === 'POSTED') {
    return (
      <Stack gap={2}>
        <Tooltip label="Requires Operations role" disabled={canOperate}>
          <span>
            <ConfirmButton
              size="xs"
              color="red"
              variant="light"
              leftSection={<IconArrowBackUp size={14} />}
              loading={reverse.isPending || createClone.isPending}
              disabled={!canOperate}
              label="Reverse & clone"
              message={`Reverse weighbridge ticket ${ticket.number}? Its status will be set to REVERSED (no stock movement — a ticket alone never posts one) and a new DRAFT copy will be created for you to redo. Blocked if a goods receipt is still POSTED against it — reverse that first.`}
              onConfirm={handleReverseAndClone}
            />
          </span>
        </Tooltip>
        {error && (
          <Text size="xs" c="red">
            {error}
          </Text>
        )}
        {cloned && (
          <Text size="xs" c="green">
            Cloned as {cloned} (DRAFT)
          </Text>
        )}
      </Stack>
    );
  }
  return null;
}

function TicketsTable() {
  const [status, setStatus] = useState<'DRAFT' | 'POSTED' | 'REVERSED' | null>(null);
  const { data, isLoading, isError } = useTickets({ status: status ?? undefined });
  const rows = useMemo(() => [...(data?.items ?? [])].reverse(), [data]);

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Title order={3}>Weighbridge tickets</Title>
        <Select
          placeholder="All statuses"
          clearable
          data={['DRAFT', 'POSTED', 'REVERSED']}
          value={status}
          onChange={(v) => setStatus(v as typeof status)}
          w={160}
        />
      </Group>
      {isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load weighbridge tickets.
        </Alert>
      )}
      <Paper withBorder radius="md">
        <Table.ScrollContainer minWidth={800}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Number</Table.Th>
                <Table.Th>Vehicle</Table.Th>
                <Table.Th>Direction</Table.Th>
                <Table.Th>Net kg</Table.Th>
                <Table.Th>Weighed at</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((t) => (
                <Table.Tr key={t.id}>
                  <Table.Td>{t.number}</Table.Td>
                  <Table.Td>{t.vehicleReg}</Table.Td>
                  <Table.Td>{t.direction}</Table.Td>
                  <Table.Td>{formatQtyDisplay(t.netKg)}</Table.Td>
                  <Table.Td>{new Date(t.weighedAt).toLocaleString()}</Table.Td>
                  <Table.Td>
                    <DocStatusBadge status={t.status} />
                  </Table.Td>
                  <Table.Td>
                    <TicketRowActions ticket={t} />
                  </Table.Td>
                </Table.Tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c="dimmed" ta="center" py="md">
                      No weighbridge tickets yet.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    </Stack>
  );
}

export function ReceiptsRegister() {
  return (
    <Stack gap="xl">
      <PageHeader
        title="Receipts"
        description="Every goods receipt and weighbridge ticket posted from Receive a load."
        actions={
          <Stack gap={2} align="flex-end">
            <Button component={Link} to="/purchasing-intake/landed-cost" variant="light" leftSection={<IconCoins size={16} />}>
              Allocate landed cost
            </Button>
            <Text size="xs" c="dimmed">
              Split a haulage/freight invoice across receipts so each product's cost includes delivery.
            </Text>
          </Stack>
        }
      />
      <GoodsReceiptsTable />
      <TicketsTable />
    </Stack>
  );
}
